import React, { useContext, useEffect, useState } from 'react';
import { ModalContext } from '../../../../../components/modals/Report';

// utilities
import { getDateTimeFromMillisecondsTZ } from '../../../../../utils/getDateFormat';

// components
import { Button, Checkbox, Typography, ImageListItemBar, Divider, IconButton } from '@common-ui/ui';
import imageStatusIconEnum from '../../../../../enums/imageStatusIconEnum';

/**
 * This component will show the images that can be selected in the Image Editor screen
 *
 * @param {Array} selectedImageIds - The selected image ids to be added to a report
 * @param {Array} setSelectedImageIds - The hook to set the selected image ids state
 * @returns
 */
export default function AddToReport({ selectedImageIds, setSelectedImageIds }) {
  const { modalData = {} } = useContext(ModalContext);
  const [selectedImages, setSelectedImages] = useState({});

  useEffect(() => {
    setSelectedImages(modalData);
  }, [modalData]);

  // This function will handle the checkbox change
  const handleCheckboxChange = (id) => {
    setSelectedImageIds((prevSelectedImages) => {
      return prevSelectedImages.includes(id)
        ? [...prevSelectedImages].filter((imageId) => imageId !== id)
        : [...prevSelectedImages, id];
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.column}>
        <Typography align="center" variant="h5">
          Select Images
        </Typography>
        <Divider />
        <div style={styles.row}>
          <Typography>Select the images you want to add to a report</Typography>
          <div style={styles.row}>
            <Button
              color="secondary"
              onClick={() =>
                setSelectedImageIds(
                  Object.keys(selectedImages).map((image) => selectedImages[image]?.DATA?.id),
                )
              }
              disabled={selectedImageIds?.length === Object.keys(modalData)?.length}
            >
              Select All
            </Button>
            <Button
              color="secondary"
              onClick={() => setSelectedImageIds([])}
              disabled={selectedImageIds.length === 0}
            >
              Clear All
            </Button>
          </div>
        </div>
        <div style={styles.images}>
          {Object.keys(selectedImages).map((image) => (
            <div
              key={selectedImages[image]?.DATA?.id}
              style={{
                position: 'relative',
              }}
            >
              {selectedImages[image] && (
                <div>
                  <Checkbox
                    checked={selectedImageIds.includes(selectedImages[image]?.DATA?.id)}
                    onChange={() => handleCheckboxChange(selectedImages[image]?.DATA?.id)}
                    style={styles.checkbox}
                  />

                  <ImageListItemBar
                    title={selectedImages[image]?.sort_info?.origin_path ?? 'Image'}
                    subtitle={
                      getDateTimeFromMillisecondsTZ(
                        selectedImages[image]?.sort_info?.date_taken,
                        selectedImages[image]?.sort_info?.user_timezone,
                      ) ?? 'Date Taken'
                    }
                    actionIcon={
                      <IconButton>
                        <img
                          src={
                            imageStatusIconEnum?.[
                              Math.floor(
                                selectedImages[image]?.DATA?.json?.IRVIEWER?.status?.code / 100,
                              )
                            ]
                          }
                        />
                      </IconButton>
                    }
                  />
                  <img src={selectedImages[image]?.DATA?.img?.src} style={styles.image} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    height: '575px',
    padding: '24px',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  image: { width: '100%', height: 'auto' },
  images: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 2,
    overflowY: 'auto',
    maxHeight: '400px',
  },
  checkbox: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 999,
  },
};
